import { getWordThemePairs } from './wordTaxonomy.js';

/**
 * Wordlist exercise (type: wordlist) body lines.
 * Canonical format: **english** [ipa] — russian
 *
 * Also accepts paste forms like:
 *   • dangerous [ˈdeɪnʤrəs] опасный
 *   dangerous — опасный
 */

const EM_DASH_SEP = ' — ';

/** Leading list markers from Word/Docs/markdown pastes. */
const BULLET_PREFIX_RE = /^\s*(?:(?:\d+[.)])|(?:[•·∙●○◦▪▫■□◆◇➢➤➜►▶→\-–—*+]))\s+/u;

/** IPA in square brackets (preferred). */
const IPA_SQUARE_RE = /\[([^\]]+)\]/;
/** Legacy IPA in slashes — still accepted when reading old bodies. */
const IPA_SLASH_RE = /\/([^/\s][^/]*[^/\s]|[^/\s])\//;

const CYRILLIC_RE = /[\u0400-\u04FF]/;

export function stripWordlistBulletPrefix(line) {
    let s = String(line || '');
    // Repeat in case of nested "• - word"
    for (let i = 0; i < 3; i += 1) {
        const next = s.replace(BULLET_PREFIX_RE, '');
        if (next === s) break;
        s = next;
    }
    return s.trim();
}

function stripMarkdownBold(s) {
    return String(s || '').replace(/\*\*/g, '').trim();
}

/**
 * Split "english [ipa] russian" / "english /ipa/ russian" (no em-dash).
 * Returns null if no IPA token is present.
 */
function splitAroundIpa(line) {
    const square = line.match(IPA_SQUARE_RE);
    if (square && square.index != null) {
        const before = line.slice(0, square.index).trim();
        const after = line.slice(square.index + square[0].length).trim();
        return { english: stripMarkdownBold(before), ipa: square[1].trim(), russian: after };
    }
    const slash = line.match(IPA_SLASH_RE);
    if (slash && slash.index != null) {
        const before = line.slice(0, slash.index).trim();
        const after = line.slice(slash.index + slash[0].length).trim();
        return { english: stripMarkdownBold(before), ipa: slash[1].trim(), russian: after };
    }
    return null;
}

/** Pull IPA out of an English-side fragment that already excludes Russian. */
function extractIpaFromEnglishSide(engPart) {
    const around = splitAroundIpa(engPart);
    if (around) {
        // Russian after IPA on the English side is unusual; append to english if any.
        const eng = [around.english, around.russian].filter(Boolean).join(' ').trim();
        return { ipa: around.ipa, english: eng };
    }
    return { ipa: '', english: stripMarkdownBold(engPart) };
}

/**
 * Split "english russian" when Russian is Cyrillic and there is no IPA/dash.
 */
function splitLatinThenCyrillic(line) {
    const m = String(line || '').match(/^(.+?)\s+(?=[\u0400-\u04FF])([\u0400-\u04FF].*)$/u);
    if (!m) return null;
    const english = stripMarkdownBold(m[1]);
    const russian = m[2].trim();
    if (!english || !russian || !CYRILLIC_RE.test(russian)) return null;
    return { english, russian, ipa: '' };
}

/**
 * Parse one wordlist body line.
 * - Word rows: `{ type:'word', english, russian, ipa }` (em-dash or dictionary paste)
 * - Group headings: `{ type:'heading', text }` — lines without ` — ` that are not
 *   dictionary-style word pastes (e.g. "Bakery & Grains")
 */
export function parseWordlistExerciseLine(line) {
    const cleaned = stripWordlistBulletPrefix(line);
    if (!cleaned) return null;

    // 1) Canonical / pasted with dash: english [ipa] — russian
    if (cleaned.includes(EM_DASH_SEP)) {
        const i = cleaned.indexOf(EM_DASH_SEP);
        const engRaw = cleaned.slice(0, i);
        const russian = cleaned.slice(i + EM_DASH_SEP.length).trim();
        const { ipa, english } = extractIpaFromEnglishSide(engRaw);
        if (!english && !russian) return null;
        return { type: 'word', english, russian, ipa };
    }

    // 2) Dictionary paste: english [ipa] russian  (no dash) — still a word row
    const aroundIpa = splitAroundIpa(cleaned);
    if (aroundIpa && aroundIpa.english) {
        return { type: 'word', ...aroundIpa };
    }

    // 3) english + Cyrillic russian, no IPA
    const cyr = splitLatinThenCyrillic(cleaned);
    if (cyr) return { type: 'word', ...cyr };

    // 4) No em-dash and not a bilingual paste → group heading
    const text = stripMarkdownBold(cleaned);
    if (!text) return null;
    return { type: 'heading', text };
}

export function isWordlistHeadingRow(row) {
    return row?.type === 'heading' || row?.isHeading === true;
}

export function parseWordlistExerciseBody(body) {
    return String(body || '')
        .split(/\r?\n/)
        .map((line) => parseWordlistExerciseLine(line))
        .filter(Boolean);
}

/**
 * Insert heading rows by `subtheme` (A–Z; empty subthemes last as "Other" when mixed).
 * Input rows are Global Words / wordlist_data objects (headings in the input are skipped).
 */
export function groupWordlistRowsBySubtheme(words) {
    const list = (Array.isArray(words) ? words : []).filter((w) => !isWordlistHeadingRow(w));
    if (!list.length) return [];

    const buckets = new Map();
    for (const w of list) {
        const pairs = getWordThemePairs(w);
        const headings = pairs.length
            ? pairs.map((pair) => pair.subtheme ? `${pair.theme} / ${pair.subtheme}` : pair.theme)
            : [''];
        [...new Set(headings)].forEach((heading) => {
            const key = String(heading || '').trim();
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push({
                ...w,
                _taxonomyOccurrenceId: `${w?.id || w?.english || 'word'}::${key}`,
            });
        });
    }

    const named = [...buckets.keys()]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const sortedKeys = buckets.has('') ? [...named, ''] : named;
    const showOther = sortedKeys.length > 1 && buckets.has('');

    const rows = [];
    for (const key of sortedKeys) {
        if (key) rows.push({ type: 'heading', text: key });
        else if (showOther) rows.push({ type: 'heading', text: 'Other' });
        rows.push(...(buckets.get(key) || []));
    }
    return rows;
}

/** Strip outer [ ] or / / so IPA can be re-wrapped safely for display/storage. */
export function stripIpaDelimiters(ipa) {
    let s = String(ipa ?? '').trim();
    if (!s) return '';
    // Peel nested/accidental delimiters: [[x]], /[x]/, etc.
    for (let i = 0; i < 3; i++) {
        const next = s.replace(/^\[+|\]+$/g, '').replace(/^\/+|\/+$/g, '').trim();
        if (next === s) break;
        s = next;
    }
    return s;
}

/** Display IPA in a single pair of square brackets (or empty string). */
export function formatIpaInBrackets(ipa) {
    const raw = stripIpaDelimiters(ipa);
    return raw ? `[${raw}]` : '';
}

/**
 * Format a parsed word for storage in Exercise Content.
 * English is always bold; IPA uses square brackets.
 */
export function formatWordlistExerciseLine({ english, russian = '', ipa = '', type, text } = {}) {
    if (type === 'heading') {
        return String(text || english || '').trim() || '';
    }
    const eng = stripMarkdownBold(english);
    if (!eng) return '';
    const rawIpa = stripIpaDelimiters(ipa);
    const ipaPart = rawIpa ? ` [${rawIpa}]` : '';
    const rus = String(russian || '').trim();
    return rus ? `**${eng}**${ipaPart}${EM_DASH_SEP}${rus}` : `**${eng}**${ipaPart}`;
}

/**
 * Paste cleanup for Wordlist: plain text, no bullets, bold English, [ipa] transcription.
 * Heading lines (no em-dash / not bilingual paste) are kept as plain titles.
 */
export function normalizeWordlistPasteText(text) {
    return String(text || '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .map((raw) => {
            // Normalize common bilingual separators to the canonical spaced em-dash.
            // Do not touch hyphens inside IPA or words — only spaced dashes.
            const line = String(raw || '')
                .replace(/\s+[–—−]\s+/g, EM_DASH_SEP)
                .replace(/\s+-\s+/g, EM_DASH_SEP);
            const parsed = parseWordlistExerciseLine(line);
            if (!parsed) return '';
            if (parsed.type === 'heading') return parsed.text;
            return formatWordlistExerciseLine(parsed);
        })
        .filter(Boolean)
        .join('\n');
}
